import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { rulesAPI } from '../services/api';

export interface GroupRuleValues {
  'presence.admin_managed': boolean;
  'leave.admin_managed': boolean;
  'export.timesheet_policy': 'inherit' | 'allow' | 'deny';
  'communication.send_credentials_email': boolean;
}

interface GroupRuleEntry {
  key: string;
  name: string;
  description: string;
  category: string;
  value_type: string;
  value: string;
  is_active: boolean;
}

interface GroupRulesPanelProps {
  groupId?: number;
  values: GroupRuleValues;
  onChange: (values: GroupRuleValues) => void;
  readOnly?: boolean;
  showPreview?: boolean;
}

const defaultValues: GroupRuleValues = {
  'presence.admin_managed': false,
  'leave.admin_managed': false,
  'export.timesheet_policy': 'inherit',
  'communication.send_credentials_email': true,
};

const GroupRulesPanel: React.FC<GroupRulesPanelProps> = ({
  groupId,
  values,
  onChange,
  readOnly = false,
  showPreview = true,
}) => {
  const [rules, setRules] = useState<GroupRuleEntry[]>([]);
  const [preview, setPreview] = useState<{ effects: string[]; visibility: Record<string, boolean> } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (groupId) {
      loadGroupRules(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId && showPreview) {
      loadPreview(groupId);
    }
  }, [groupId, values, showPreview]);

  const loadGroupRules = async (id: number) => {
    setLoading(true);
    try {
      const res = await rulesAPI.getGroupRules(id);
      const loaded = { ...defaultValues };
      (res.rules || []).forEach((r: GroupRuleEntry) => {
        if (r.key === 'presence.admin_managed') loaded['presence.admin_managed'] = r.value === 'true';
        if (r.key === 'leave.admin_managed') loaded['leave.admin_managed'] = r.value === 'true';
        if (r.key === 'export.timesheet_policy') loaded['export.timesheet_policy'] = r.value as 'inherit' | 'allow' | 'deny';
        if (r.key === 'communication.send_credentials_email') loaded['communication.send_credentials_email'] = r.value === 'true';
      });
      onChange(loaded);
      setRules(res.rules || []);
    } catch {
      // usa valori passati dal parent
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (id: number) => {
    try {
      const res = await rulesAPI.getGroupRulesPreview(id);
      setPreview({ effects: res.effects || [], visibility: res.visibility_preview || res.visibility || {} });
    } catch {
      setPreview(computeLocalPreview(values));
    }
  };

  const computeLocalPreview = (v: GroupRuleValues) => {
    const presence = v['presence.admin_managed'];
    const leave = v['leave.admin_managed'];
    const effects: string[] = [];
    if (presence) {
      effects.push('Il dipendente non può timbrare autonomamente');
      effects.push('Timbratura mobile e cartellino mensile nascosti nell\'area riservata');
    }
    if (leave) {
      effects.push('Il dipendente non può creare richieste ferie/permessi');
    }
    if (!v['communication.send_credentials_email']) {
      effects.push('Le email di credenziali non vengono inviate automaticamente');
    }
    if (v['export.timesheet_policy'] === 'deny') {
      effects.push('Export cartellino disabilitato');
    }
    return {
      effects,
      visibility: {
        show_mobile_checkin: !presence,
        show_monthly_timesheet: !presence,
        show_dashboard_checkin: !presence,
        show_leave_request_form: !leave,
        show_timesheet_export: v['export.timesheet_policy'] !== 'deny',
      },
    };
  };

  const update = (patch: Partial<GroupRuleValues>) => {
    const next = { ...values, ...patch };
    if (patch['presence.admin_managed'] === true && !('communication.send_credentials_email' in patch)) {
      // suggerimento default: cartellino admin spesso implica no email automatica
    }
    onChange(next);
    if (!groupId) {
      setPreview(computeLocalPreview(next));
    }
  };

  if (loading && groupId) {
    return <CircularProgress size={24} />;
  }

  const activePreview = preview || computeLocalPreview(values);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Presenze e cartellino
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={values['presence.admin_managed']}
            onChange={(e) => update({ 'presence.admin_managed': e.target.checked })}
            disabled={readOnly}
          />
        }
        label="Admin gestisce i cartellini per i dipendenti di questo gruppo"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
        {rules.find((r) => r.key === 'presence.admin_managed')?.description ||
          'Il dipendente non può timbrare; l\'admin inserisce le presenze.'}
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Ferie e permessi
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={values['leave.admin_managed']}
            onChange={(e) => update({ 'leave.admin_managed': e.target.checked })}
            disabled={readOnly}
          />
        }
        label="Admin gestisce ferie e permessi per i dipendenti di questo gruppo"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
        Il dipendente può consultare saldo e storico ma non creare nuove richieste.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Export cartellino
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }} disabled={readOnly}>
        <InputLabel>Policy export cartellino operatore</InputLabel>
        <Select
          value={values['export.timesheet_policy']}
          label="Policy export cartellino operatore"
          onChange={(e) => update({ 'export.timesheet_policy': e.target.value as 'inherit' | 'allow' | 'deny' })}
        >
          <MenuItem value="inherit">Eredita globale</MenuItem>
          <MenuItem value="allow">Consenti sempre</MenuItem>
          <MenuItem value="deny">Nega sempre</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Comunicazioni
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={values['communication.send_credentials_email']}
            onChange={(e) => update({ 'communication.send_credentials_email': e.target.checked })}
            disabled={readOnly}
          />
        }
        label="Invia email credenziali/attivazione ai dipendenti del gruppo"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
        Se disattivato, le credenziali vengono generate ma l&apos;admin deve comunicarle manualmente.
      </Typography>

      {showPreview && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Effetto nell&apos;area riservata del dipendente
          </Typography>
          <List dense disablePadding>
            {activePreview.effects.map((effect, idx) => (
              <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircleOutlineIcon fontSize="small" color="info" />
                </ListItemIcon>
                <ListItemText primary={effect} />
              </ListItem>
            ))}
          </List>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Visibilità menu: timbratura mobile{' '}
            {activePreview.visibility.show_mobile_checkin ? 'sì' : 'no'}, cartellino mensile{' '}
            {activePreview.visibility.show_monthly_timesheet ? 'sì' : 'no'}, check-in dashboard{' '}
            {activePreview.visibility.show_dashboard_checkin ? 'sì' : 'no'}, richieste ferie{' '}
            {activePreview.visibility.show_leave_request_form ? 'sì' : 'no'}, export{' '}
            {activePreview.visibility.show_timesheet_export ? 'sì' : 'no'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default GroupRulesPanel;
export { defaultValues as defaultGroupRuleValues };
